<template>
  <section class="hotel-list">
    <header class="hotel-list__header">
      <div class="hotel-list__total">{{ $fmt(totalFoundMessageFormat, total) }}</div>
      <HotelDirectoryListSortControl :value="sortOrder" @update:value="onChangeSortOrder" />
    </header>
    <template v-if="hotels.length > 0">
      <ul class="hotel-list__list">
        <li v-for="hotel in hotels" :key="hotel.id" class="hotel-list__hotel">
          <HotelDirectoryListItem :hotel="hotel" :hotel-attributes="hotelAttributes" />
        </li>
      </ul>
      <PaginationControl
        :model-value="currentPage"
        :per-page="perPage"
        :total-rows="total"
        :limit="8"
        @update:model-value="onPageChange"
      />
    </template>
    <div v-else class="hotel-list__empty">
      <SvgIcon icon="pic-exclamation-mark" width="150" height="150" view-box="0 0 150 150" />
      <p v-html="$res.Views.LocalHotelSearch_HotelsNotFound"></p>
      <div>
        <BaseButton size="max" @click="$emit('clear-filters')">{{ $res.General.ClearFilters }}</BaseButton>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, watch } from "vue";
import HotelDirectoryListItem from "./HotelDirectoryListItem.vue";
import HotelDirectoryListSortControl from "./HotelDirectoryListSortControl.vue";
import PaginationControl from "@/common/controls/PaginationControl.vue";
import BaseButton from "@/common/controls/BaseButton.vue";
import SvgIcon from "@/common/controls/SvgIconControl.vue";

const props = defineProps({
  hotels: { type: Array, required: true },
  hotelAttributes: { type: Array, required: true },
  total: { type: Number, required: true },
  perPage: { type: Number, required: true },
  totalFoundMessageFormat: { type: String, required: true },
  page: { type: Number, default: 1 },
  sort: { type: String, default: "name-asc" },
});

const emit = defineEmits(["sort-changed", "page-changed", "clear-filters"]);

const sortOrder = ref(props.sort);
const currentPage = ref(props.page);

watch(() => props.sort, (value) => { sortOrder.value = value; });
watch(() => props.page, (value) => { currentPage.value = value; });

function onPageChange(page) {
  if (currentPage.value !== page) {
    currentPage.value = page;
    emit("page-changed", page);
  }
}

function onChangeSortOrder(value) {
  if (sortOrder.value !== value) {
    sortOrder.value = value;
    emit("sort-changed", value);
  }
}
</script>

<style scoped lang="scss">
.hotel-list__list {
  list-style-type: none;
  padding: 0;
  margin: 10px 0;
}

.hotel-list__hotel {
  border-radius: var(--border-radius-lg);
  overflow: hidden;
  box-shadow: var(--shadow);

  + .hotel-list__hotel {
    margin-top: 10px;
  }
}

.hotel-list__header {
  display: flex;

  > .sort {
    margin-inline-start: auto;
  }
}

.hotel-list__total {
  color: var(--second-20);
}

.hotel-list__empty {
  background-color: var(--white);
  border-radius: var(--border-radius-lg);
  overflow: hidden;
  box-shadow: var(--shadow);
  margin-top: 10px;
  padding: 40px 0;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 30px;

  p {
    font-size: 16px;
    line-height: 24px;
    margin: 0;
  }
}
</style>
